/* @flow */
import bisect from "binary-search";
import memoizeOne from "memoize-one";
import type { Stock, Deal } from "../../redux/CommonTypes";
import * as Types from "./NextGenChartTypes";
import memoize from "lodash/memoize";

// Каждый сегмент получает уникальный ключ
// на основе масштаба и позиции относительно
// нулевой даты. Так мы можем агрессивно ке-
// шировать точки.
export function getSectionKey({
  stock,
  interval,
  length,
  offset
}: Types.Section) {
  return `${stock}-${interval}-${length}-${offset}`;
}

export function getSectionBoundaries(section: Types.Section): [number, number] {
  return [
    section.length * section.offset,
    section.length * (section.offset + 1)
  ];
}

const NORMAL_HISTORY_LENGTH = (1000 * 60 * 60 * 24 * 365) / 2;
const SHORT_HISTORY_LENGTH = 1000 * 60 * 60 * 3;

export function isShortHistoryStock(stock: Stock): boolean {
  return stock.longHistory === false;
}

export function getHistoryLength(stock: Stock): number {
  return isShortHistoryStock(stock)
    ? SHORT_HISTORY_LENGTH
    : NORMAL_HISTORY_LENGTH;
}

export function whatTimeIsIt(props: Types.PayloadProps): number {
  return props.realtimeCache[props.realtimeCache.length - 1].date;
}

export const intervals = [
  1000, // Секунда
  1000 * 5,
  1000 * 10,
  1000 * 15,
  1000 * 30,
  1000 * 60, // Минута
  1000 * 60 * 2,
  1000 * 60 * 5,
  1000 * 60 * 10,
  1000 * 60 * 15,
  1000 * 60 * 30,
  1000 * 60 * 60, // Час
  1000 * 60 * 60 * 2,
  1000 * 60 * 60 * 4,
  1000 * 60 * 60 * 8,
  1000 * 60 * 60 * 12,
  1000 * 60 * 60 * 24, // День
  1000 * 60 * 60 * 24 * 7, // Неделя
  1000 * 60 * 60 * 24 * 30 // Месяц
];

const multipliers = {
  s: 1000,
  m: 1000 * 60,
  h: 1000 * 60 * 60,
  D: 1000 * 60 * 60 * 24,
  W: 1000 * 60 * 60 * 24 * 7,
  M: 1000 * 60 * 60 * 24 * 30,
  Y: 1000 * 60 * 60 * 24 * 365
};

export function parseTime(timeAsString: string): number {
  const quantity = parseInt(timeAsString, 10);
  const multiplier = multipliers[timeAsString.slice(-1)];

  return quantity * multiplier;
}

export function stringifyTime(timeAsNumber: number): string {
  const keys = Object.keys(multipliers);

  for (let i = 0; i < keys.length - 1; i++) {
    if (
      timeAsNumber >= multipliers[keys[i]] &&
      timeAsNumber < multipliers[keys[i + 1]]
    ) {
      return Math.floor(timeAsNumber / multipliers[keys[i]]) + keys[i];
    }
  }

  return Math.floor(timeAsNumber / multipliers["Y"]) + "Y";
}

export function getTimeSpanRange(
  stock: Stock,
  interval: number
): [number, number] {
  // Ограничиваем таймфреймы так,
  // чтобы на экране никогда не оказалось
  // меньше 10 свечей и больше 50.

  return [interval * 8, Math.min(interval * 100, getHistoryLength(stock))];
}

export const getCommonSectionProps = memoize(function(
  props: Types.PayloadProps
): Types.CommonSectionProps {
  let actualInterval;

  if (props.type === "line") {
    // Для начала, сколько точек вмещает экран?
    const pointsPerScreen = props.width * props.density;

    // Если бы интервал изменялся плавно, мы бы
    // просто вычислили его так:
    const idealInterval = props.timeSpan / pointsPerScreen;

    // Однако наши интервалы изменяются степенями
    // двойки, чтобы не делать много разнообразных
    // запросов к серверу.
    actualInterval = intervals[0];

    for (const candidate of intervals) {
      if (actualInterval >= idealInterval) {
        break;
      }

      actualInterval = candidate;
    }
  } else {
    actualInterval = props.interval * 1000;
  }

  // От этого и точек на экране не всегда столько,
  // сколько хотелось бы. Но это ничего!
  // const actualPPS = Math.ceil(props.timeSpan / actualInterval);

  // Зная, сколько мы хотим точек, можно поровну
  // разбить их по секциям. Округляем в большую
  // сторону, чтобы не обрывать график перед кра-
  // ем экрана.

  const actualTimeSpan =
    props.scaleX.invert(props.width) - props.scaleX.invert(0);

  const sectionLength = actualInterval * props.sectionSize;
  const sections = Math.ceil(actualTimeSpan / sectionLength);

  return {
    interval: actualInterval,
    length: sectionLength,
    quantity: sections
  };
});

export function generateSections(
  common: Types.CommonSectionProps,
  props: Types.PayloadProps
): Array<Types.Section> {
  // Время в левой части экрана.
  const earliestVisibleTime = props.scaleX.invert(0);

  // Индекс крайней левой секции относительно нулевого времени!
  const section0Offset = Math.floor(earliestVisibleTime / common.length) - 1;

  const sections = [];

  for (let index = 0; index <= common.quantity + 1; index++) {
    const section = {
      interval: common.interval,
      length: common.length,
      stock: props.stock,
      offset: section0Offset + index
    };

    const type = getSectionType(section, props);

    switch (type) {
      case "past":
        sections.push(section);
        break;
      case "present":
        sections.push(section);
        break;
      case "future":
      case "weekend":
      default:
        break;
    }
  }

  return sections;
}

export function getSections(props: Types.PayloadProps): Array<Types.Section> {
  const common = getCommonSectionProps(props);
  return generateSections(common, props);
}

export function getSectionType(
  section: Types.Section,
  props: Types.PayloadProps
): Types.SectionType {
  const now = whatTimeIsIt(props);
  const [start, end] = getSectionBoundaries(section);

  if (now > start && now < end) {
    return "present";
  } else if (now >= end) {
    if (props.checkpoints.length === 0) {
      return "past";
    }

    const bisectedIndex = bisect(
      props.checkpoints,
      start,
      (checkpoint, value) => checkpoint.value - value
    );

    switch (true) {
      case bisectedIndex === -1:
        // Начало секции раньше любого выходного
        return "past";
      case bisectedIndex > -1: {
        // Начало секции попадает на начало или конец выходного
        const checkpoint = props.checkpoints[bisectedIndex];

        if (checkpoint.type === "negative" && end <= checkpoint.source[1]) {
          return "weekend";
        } else {
          return "past";
        }
      }
      default: {
        // Начало секции где-то между чекпоинтами
        const checkpoint = props.checkpoints[-bisectedIndex - 2];

        if (checkpoint.type === "negative" && end <= checkpoint.source[1]) {
          return "weekend";
        } else {
          return "past";
        }
      }
    }
  } else {
    return "future";
  }
}

export function groupRealtimePoints(
  section: Types.Section,
  points: Array<Types.RealtimePoint>
): Array<Types.Point> {
  if (points.length === 0) {
    return [];
  }

  const result = [];

  let nextMilestone =
    Math.ceil(points[0].date / section.interval) * section.interval;

  let currentBatch = {
    epoch: nextMilestone / 1000,
    open: points[0].value,
    close: points[0].value,
    min: points[0].value,
    max: points[0].value
  };

  result.push(currentBatch);

  for (const point of points) {
    if (point.date > nextMilestone) {
      nextMilestone =
        Math.ceil(point.date / section.interval) * section.interval;

      currentBatch = {
        epoch: nextMilestone / 1000,
        open: point.value,
        close: point.value,
        min: point.value,
        max: point.value
      };

      result.push(currentBatch);
    }

    currentBatch.close = point.value;
    currentBatch.min = Math.min(point.value, currentBatch.min);
    currentBatch.max = Math.max(point.value, currentBatch.max);
  }

  return result;
}

function getPresentSectionPoints(
  section: Types.Section,
  cache: { [string]: Array<Types.Point> },
  props: Types.PayloadProps
): Array<Types.Point> {
  const key = getSectionKey(section);
  const history =
    cache[key] === "void" || cache[key] === "darkness" || cache[key] == null
      ? []
      : cache[key];

  if (history.length > 0) {
    // Junction это точка, соединяющая реальное время с историей.
    const junctionHistoryIndex = history.length - 1;
    const junctionDate = history[junctionHistoryIndex].epoch * 1000;

    // Для поиска нужной точки в кеше мы используем бинарный поиск.
    const potentialJunctionRealtimeIndex = bisect(
      props.realtimeCache,
      junctionDate,
      (item, date) => item.date - date
    );

    // Если бинарный поиск не нашёл точный результат, он возвращает
    // индекс, где этот результат должен был лежать, только в виде от-
    // рицательного заначения.
    // Нам неточный результат тоже сгодится.
    const junctionRealtimeIndex =
      potentialJunctionRealtimeIndex > 0
        ? potentialJunctionRealtimeIndex
        : -potentialJunctionRealtimeIndex;

    return [
      ...history,
      ...groupRealtimePoints(
        section,
        props.realtimeCache.slice(junctionRealtimeIndex)
      )
    ];
  } else {
    const [junctionDate] = getSectionBoundaries(section);

    // TODO: спрятать дублирование
    const potentialJunctionRealtimeIndex = bisect(
      props.realtimeCache,
      junctionDate,
      (item, date) => item.date - date
    );

    const junctionRealtimeIndex =
      potentialJunctionRealtimeIndex > 0
        ? potentialJunctionRealtimeIndex
        : -potentialJunctionRealtimeIndex;

    return groupRealtimePoints(
      section,
      props.realtimeCache.slice(junctionRealtimeIndex)
    );
  }
}

function getPointsFromCache(
  section: Types.Section,
  cache: { [string]: Array<Types.Point> }
): Array<Types.Point> | "void" {
  const key = getSectionKey(section);
  return cache[key] || [];
}

const getSectionPointsWithRealtimePriority = function(
  section: Types.Section,
  cache: { [string]: Array<Types.Point> },
  realtimeCache: Array<Types.RealtimePoint>
): Array<Types.Point> | "void" {
  const [start, end] = getSectionBoundaries(section);
  const historical = getPointsFromCache(section, cache);

  if (realtimeCache.length === 0) {
    // Кеша нет и мы идём по простому пути
    return historical;
  } else {
    const firstAvailableRealtimePoint = realtimeCache[0];

    if (firstAvailableRealtimePoint.date > end) {
      // Зелёная историческая секция
      return historical;
    } else if (firstAvailableRealtimePoint.date <= start) {
      // Секцию можно построить целиком из риалтайма (красная)
      return groupRealtimePoints(
        section,
        realtimeCache.filter(point => point.date >= start && point.date <= end)
      );
    } else {
      // Секцию придётся клеить из двух источников (жёлтая)
      return getPresentSectionPoints(section, cache, { realtimeCache });
    }
  }

  // That's all folks
};

window.prioritizeRealtime = true;

export function getSectionPoints(
  section: Types.Section,
  cache: { [string]: Array<Types.Point> },
  props: Types.PayloadProps
): Array<Types.Point> | "void" {
  const type = getSectionType(section, props);

  switch (type) {
    case "past":
      return window.prioritizeRealtime
        ? getSectionPointsWithRealtimePriority(
            section,
            cache,
            props.realtimeCache
          )
        : getPointsFromCache(section, cache);
    case "future":
      return [];
    case "present":
      return window.prioritizeRealtime
        ? getSectionPointsWithRealtimePriority(
            section,
            cache,
            props.realtimeCache
          )
        : getPresentSectionPoints(section, cache, props);
    default:
      return [];
  }
}

function expensive_getOperationPoints(operations) {
  let points = [];

  for (let operation of operations) {
    points.push({
      epoch: operation.open_timestamp,
      open: operation.value,
      close: operation.value,
      min: operation.value,
      max: operation.value
    });

    if (operation.close_value != null && operation.close_timestamp != null) {
      points.push({
        epoch: operation.close_timestamp,
        open: operation.close_value,
        close: operation.close_value,
        min: operation.close_value,
        max: operation.close_value
      });
    }
  }

  return points.sort((pointA, pointB) => pointA.epoch - pointB.epoch);
}

let getOperationPoints = memoizeOne(expensive_getOperationPoints);

function sortedMerge(a, b) {
  if (a.length === 0) {
    return b;
  } else if (b.length === 0) {
    return a;
  }

  let result = [];
  let indexInA = 0;
  let indexInB = 0;

  while (indexInA < a.length) {
    let milestone = a[indexInA];
    let skipMilestone = false;

    while (indexInB < b.length) {
      let candidate = b[indexInB];

      if (candidate["epoch"] < milestone["epoch"]) {
        result.push(candidate);
        indexInB++;
      } else if (candidate["epoch"] === milestone["epoch"]) {
        result.push(candidate);
        indexInB++;
        skipMilestone = true;
        break;
      } else {
        break;
      }
    }

    if (!skipMilestone) {
      result.push(milestone);
    }
    indexInA++;
  }

  while (indexInB < b.length) {
    result.push(b[indexInB]);
    indexInB++;
  }

  return result;
}

export function getPoints(
  sections: Array<Types.Section>,
  cache: { [string]: Array<Types.Point> },
  props: Types.PayloadProps,
  deals: Array<Deal>
): {
  points: Array<Types.Point>,
  emptySections: Array<Types.Section>,
  isDarkArea: boolean
} {
  // TODO: есть потенциал оптимизировать память и итерации

  let result = [];
  let emptySections = [];
  let isDarkArea = false;

  for (const section of sections) {
    const points = getSectionPoints(section, cache, props);

    if (points === "void") {
      emptySections.push(section);
    } else if (points === "darkness") {
      isDarkArea = true;
    } else {
      for (const point of points) {
        const length = result.length;

        if (length === 0 || point.epoch > result[length - 1].epoch) {
          result.push(point);
        }
      }
    }
  }

  if (props.type === "line") {
    let operationPoints = getOperationPoints(deals);

    return {
      points: sortedMerge(result, operationPoints),
      rawPoints: result,
      emptySections,
      isDarkArea
    };
  } else {
    return {
      points: result,
      emptySections,
      isDarkArea
    };
  }
}

export function generateRenderablePoints(
  points: Array<Types.Point>,
  scaleX: Types.Scale,
  scaleY: Types.Scale,
  stock: string,
  interval: number,
  present: Types.RealtimePoint,
  deals: Array<Deal>
): Array<Types.RenderablePoint> {
  const result = [];

  // let dealIndexToStartWith = deals.length;

  for (let i = 0; i < points.length; i++) {
    const p = points[i];

    let date = p.epoch * 1000;
    let open = p.open;
    let low = p.min;
    let high = p.max;
    let value = p.close;

    // Это условие одновременно не позволяет сгруппированным точкам залезать
    // в будущее, а также анимирует нам последнюю точку, ведь значения в present
    // интерполируются перед попаданием сюда.

    if (date >= present.date) {
      date = present.date;
      value = present.value;
    }

    result.push([
      scaleX(date),
      scaleY(value),
      {
        stock,
        date,
        open,
        low,
        high,
        value
      }
    ]);

    // for (let j = dealIndexToStartWith - 1; j >= 0; j--) {
    //   const deal = deals[j];
    //   const dealDate = Number(deal.open_timestamp) * 1000;
    //
    //   if (
    //     dealDate > date &&
    //     points[i + 1] != null &&
    //     dealDate < points[i + 1].epoch * 1000
    //   ) {
    //     result.push([
    //       scaleX(dealDate),
    //       scaleY(deal.value),
    //       {
    //         stock,
    //         date: dealDate,
    //         value: deal.value
    //       }
    //     ]);
    //
    //     dealIndexToStartWith = j;
    //   }
    // }
  }

  return result;
}

export function generateCandles(
  points: Array<Types.Point>,
  scaleX: Types.Scale,
  scaleY: Types.Scale,
  stock: string,
  interval: number
): Array<Types.RenderableCandle> {
  return points.map((point, index, points) => {
    const previous =
      index === 0
        ? {
            epoch: point.epoch - interval / 1000,
            open: point.open,
            min: point.open,
            max: point.open,
            close: point.open
          }
        : points[index - 1];

    return {
      range: [scaleX(previous.epoch * 1000), scaleX(point.epoch * 1000)],
      open: [
        scaleX(previous.epoch),
        scaleY(previous.close),
        {
          stock,
          date: previous.epoch,
          value: previous.close
        }
      ],
      close: [
        scaleX(point.epoch * 1000),
        scaleY(point.close),
        {
          stock,
          date: point.epoch * 1000,
          value: point.close
        }
      ],
      minimum: [
        scaleX(point.epoch * 1000),
        scaleY(Math.min(point.min, previous.close)),
        {
          stock,
          date: point.epoch * 1000,
          value: point.min
        }
      ],
      maximum: [
        scaleX(point.epoch * 1000),
        scaleY(Math.max(point.max, previous.close)),
        {
          stock,
          date: point.epoch * 1000,
          value: point.max
        }
      ]
    };
  });
}


export function generateHeikenAshiCandles(
  points: Array<Types.Point>,
  scaleX: Types.Scale,
  scaleY: Types.Scale,
  stock: string,
  interval: number
): Array<Types.RenderableCandle> {
  let lastHA;

  return points.map((point, index, points) => {
    const previous =
      index === 0
        ? {
            epoch: point.epoch - interval / 1000,
            open: point.open,
            min: point.min,
            max: point.max,
            close: point.close
          }
        : points[index - 1];

    const maxValue = Math.max(
      previous.open,
      previous.close,
      previous.min,
      previous.max,
    );

    const maxLength = maxValue.toString().length;

    const lengthFixer = value => {
      if (value.toString().length > maxLength) {
        return +value.toFixed(4);
      }

      return value;
    };

    let previousHA = previous;

    if (lastHA) {
      previousHA = {
        open: lastHA.open[2].value,
        close: lastHA.close[2].value,
      }
    }

    const open = (previousHA.open + previousHA.close) / 2;
    const close = (point.open + point.close + point.min + point.max) / 4;
    const min = Math.min(point.min, open, close);
    const max = Math.max(point.max, open, close);

    lastHA = {
      range: [scaleX(previous.epoch * 1000), scaleX(point.epoch * 1000)],
      open: [
        scaleX(previous.epoch),
        scaleY(open),
        {
          stock,
          date: previous.epoch,
          value: lengthFixer(open)
        }
      ],
      close: [
        scaleX(point.epoch * 1000),
        scaleY(close),
        {
          stock,
          date: point.epoch * 1000,
          value: lengthFixer(close)
        }
      ],
      minimum: [
        scaleX(point.epoch * 1000),
        scaleY(min),
        {
          stock,
          date: point.epoch * 1000,
          value: lengthFixer(min)
        }
      ],
      maximum: [
        scaleX(point.epoch * 1000),
        scaleY(max),
        {
          stock,
          date: point.epoch * 1000,
          value: lengthFixer(max)
        }
      ]
    };

    return lastHA;
  });
}

export function generateBars(
  points: Array<Types.BarPoint>,
  scaleX: Types.Scale,
  scaleY: Types.Scale,
  interval: number
): Array<Types.RenderableCandle> {
  return points.map((point, index, points) => {
    const [date, value] = point;

    const previous =
      index === 0 ? [date - interval / 1000, value] : points[index - 1];

    return {
      range: [scaleX(previous[0]), scaleX(date)],
      point: [
        scaleX(previous[0]),
        scaleY(previous[1]),
        {
          date: previous[0],
          value: previous[1]
        }
      ]
    };
  });
}



// WEBPACK FOOTER //
// ./src/components/Chart/NextGenChartMath.js